export const DEFAULT_OG_IMAGE = '/assets/images/og-image.png';
export const SITE_URL = 'https://wagerwatchdog.com';

export type BlogPostFrontmatter = {
  title: string;
  slug: string;
  date: string;
  updated?: string;
  excerpt: string;
  seoTitle?: string;
  seoDescription?: string;
  image?: string;
  imageAlt?: string;
  author?: string;
  tags?: string[];
  category?: string;
  canonical?: string;
  weekStart?: string;
  weekEnd?: string;
  draft?: boolean;
};

export type BlogPost = BlogPostFrontmatter & {
  body: string;
  filePath: string;
};

const rawModules = import.meta.glob('../../content/blog/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

/** ponytail: minimal frontmatter parse — avoids bundling gray-matter in client JS */
function parseFrontmatter(raw: string): { data: Record<string, unknown>; body: string } {
  if (!raw.startsWith('---')) {
    return { data: {}, body: raw.trim() };
  }
  const end = raw.indexOf('---', 3);
  if (end === -1) {
    return { data: {}, body: raw.trim() };
  }
  const yamlBlock = raw.slice(3, end).trim();
  const body = raw.slice(end + 3).trim();
  const data: Record<string, unknown> = {};

  let currentKey: string | null = null;
  let listItems: string[] | null = null;

  for (const line of yamlBlock.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    const listMatch = line.match(/^\s+-\s+(.+)$/);
    if (listMatch && currentKey) {
      listItems = listItems ?? [];
      listItems.push(listMatch[1].trim().replace(/^["']|["']$/g, ''));
      data[currentKey] = listItems;
      continue;
    }

    const kv = trimmed.match(/^([\w]+):\s*(.*)$/);
    if (!kv) continue;

    currentKey = kv[1];
    const value = kv[2].trim();
    listItems = null;

    if (value === '') {
      listItems = [];
      data[currentKey] = listItems;
      continue;
    }
    if (value === 'true') {
      data[currentKey] = true;
    } else if (value === 'false') {
      data[currentKey] = false;
    } else {
      data[currentKey] = value.replace(/^["']|["']$/g, '');
    }
  }

  return { data, body };
}

function parsePost(filePath: string, raw: string): BlogPost {
  const { data, body } = parseFrontmatter(raw);
  const fm = data as Partial<BlogPostFrontmatter>;
  const slug =
    fm.slug ??
    filePath
      .split('/')
      .pop()!
      .replace(/\.md$/, '');

  return {
    title: fm.title ?? slug,
    slug,
    date: fm.date ?? new Date().toISOString().slice(0, 10),
    updated: fm.updated,
    excerpt: fm.excerpt ?? '',
    seoTitle: fm.seoTitle,
    seoDescription: fm.seoDescription,
    image: fm.image,
    imageAlt: fm.imageAlt,
    author: fm.author ?? 'IvoryShoulder',
    tags: (fm.tags as string[]) ?? [],
    category: fm.category,
    canonical: fm.canonical,
    weekStart: fm.weekStart,
    weekEnd: fm.weekEnd,
    draft: fm.draft === true,
    body,
    filePath,
  };
}

const allPosts = Object.entries(rawModules).map(([path, raw]) =>
  parsePost(path, raw),
);

export function getAllPosts(includeDrafts = false): BlogPost[] {
  return allPosts
    .filter((p) => includeDrafts || !p.draft)
    .sort((a, b) => b.date.localeCompare(a.date));
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return getAllPosts().find((p) => p.slug === slug);
}

export function postPageTitle(post: BlogPost): string {
  return post.seoTitle ?? `${post.title} | WagerWatchdog Blog`;
}

export function postDescription(post: BlogPost): string {
  return post.seoDescription ?? post.excerpt;
}

export function postCanonicalUrl(post: BlogPost): string {
  const override = post.canonical?.trim();
  if (override) {
    return override;
  }
  return `${SITE_URL}/blog/${post.slug}`;
}

export function postOgImage(post: BlogPost): string {
  const image = post.image ?? DEFAULT_OG_IMAGE;
  return image.startsWith('http') ? image : `${SITE_URL}${image}`;
}

export function postDateModified(post: BlogPost): string {
  return post.updated ?? post.date;
}

export function blogPostingSchema(post: BlogPost) {
  const url = postCanonicalUrl(post);
  const image = postOgImage(post);
  const authorName = post.author ?? 'IvoryShoulder';

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: postDescription(post),
    datePublished: post.date,
    dateModified: postDateModified(post),
    author: {
      '@type': authorName === 'IvoryShoulder' ? 'Organization' : 'Person',
      name: authorName,
    },
    publisher: {
      '@type': 'Organization',
      name: 'IvoryShoulder, LLC',
      alternateName: 'WagerWatchdog',
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/assets/images/ww_logo_dark.png`,
      },
    },
    image,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    isPartOf: {
      '@type': 'WebSite',
      name: 'WagerWatchdog',
      url: SITE_URL,
    },
    url,
    keywords: post.tags?.join(', '),
    articleSection: post.category,
  };
}
